
import { useState, useEffect } from 'react';
import { Constituency, FormValues } from './types';
import { supabase } from '@/integrations/supabase/client';
import { useCountry } from '@/contexts/CountryContext';

export const useConstituencies = () => {
  const { availableCountries } = useCountry();
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConstituency, setEditingConstituency] = useState<Constituency | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch constituencies
  useEffect(() => {
    fetchConstituencies();
  }, [selectedCountry]);

  const fetchConstituencies = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('constituencies')
        .select('*')
        .order('name', { ascending: true });
      
      if (selectedCountry) {
        query = query.eq('country_code', selectedCountry);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setConstituencies(data || []);
    } catch (error) {
      console.error('Error fetching constituencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingConstituency(null);
    setDialogOpen(true);
  };

  const handleEdit = (constituency: Constituency) => {
    setEditingConstituency(constituency);
    setDialogOpen(true);
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      if (editingConstituency) {
        // Update existing
        const { error } = await supabase
          .from('constituencies')
          .update({
            name: values.name,
            code: values.code,
            country_code: values.country_code,
            active: values.active
          })
          .eq('id', editingConstituency.id);
        
        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('constituencies')
          .insert({
            name: values.name,
            code: values.code,
            country_code: values.country_code,
            active: values.active
          });
        
        if (error) throw error;
      }
      
      setDialogOpen(false);
      fetchConstituencies();
    } catch (error: any) {
      console.error('Error saving constituency:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this constituency?')) {
      return;
    }
    
    try {
      const { error } = await supabase
        .from('constituencies')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      fetchConstituencies();
    } catch (error: any) {
      console.error('Error deleting constituency:', error);
    }
  };

  const toggleActive = async (constituency: Constituency) => {
    try {
      const { error } = await supabase
        .from('constituencies')
        .update({ active: !constituency.active })
        .eq('id', constituency.id);
      
      if (error) throw error;
      
      fetchConstituencies();
    } catch (error: any) {
      console.error('Error toggling constituency status:', error);
    }
  };

  // Filter constituencies by search term
  const filteredConstituencies = constituencies.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return {
    constituencies: filteredConstituencies,
    loading,
    selectedCountry,
    setSelectedCountry,
    dialogOpen,
    setDialogOpen,
    editingConstituency,
    handleAddNew,
    handleEdit,
    handleSubmit,
    handleDelete,
    toggleActive,
    searchTerm,
    setSearchTerm,
  };
};
